/* eslint camelcase: ["error", {"allow": ["shortcut_id", "opened_by_creator", "view_id", "placement_owner_type", "search_type"]}] -- Analytics schema fields. */
import {useAnalytics} from '@github-ui/use-analytics'
import {useCallback} from 'react'

import type {SavedViewOpenSource} from '../saved-view-open-source'
import type {
  SavedViewId,
  SavedViewVisibility,
  ViewIndexMetadata,
  ViewNewVisitMetadata,
  ViewOpenMetadata,
  ViewVisibility,
} from '../types'

const ANALYTICS_TARGET = 'REPOSITORY_SAVED_VIEW'
const VIEW_ANALYTICS_TARGET = 'SAVED_VIEW'

export const VIEW_EVENTS = {
  indexVisited: 'saved_view.index',
  newVisited: 'saved_view.new',
  viewOpened: 'saved_view.open',
} as const

export const SAVED_VIEW_EVENTS = {
  indexVisited: 'repository_saved_view.index',
  newVisited: 'repository_saved_view.new',
  viewOpened: 'repository_saved_view.open',
  editOpened: 'repository_saved_view.edit',
  viewUpdated: 'repository_saved_view.update',
  viewPinned: 'repository_saved_view.pin',
  viewUnpinned: 'repository_saved_view.unpin',
} as const

export const SAVED_VIEW_UPDATE_SOURCES = {
  edit: 'edit',
  query: 'query',
} as const

export type SavedViewUpdateSource = (typeof SAVED_VIEW_UPDATE_SOURCES)[keyof typeof SAVED_VIEW_UPDATE_SOURCES]

export const SAVED_VIEW_PIN_SOURCES = {
  indexRowMenu: 'index_row_menu',
  individualViewActionsMenu: 'individual_view_actions_menu',
} as const

export type SavedViewPinSource = (typeof SAVED_VIEW_PIN_SOURCES)[keyof typeof SAVED_VIEW_PIN_SOURCES]

function viewVisibility(visibility: SavedViewVisibility): ViewVisibility {
  return visibility === 'personal' ? 'private' : 'shared'
}

export function useSavedViewAnalytics() {
  const {sendAnalyticsEvent} = useAnalytics()

  const trackIndexVisited = useCallback(
    (viewMetadata?: ViewIndexMetadata) => {
      if (!viewMetadata) {
        sendAnalyticsEvent(SAVED_VIEW_EVENTS.indexVisited, ANALYTICS_TARGET)
        return
      }

      sendAnalyticsEvent(VIEW_EVENTS.indexVisited, VIEW_ANALYTICS_TARGET, {
        placement_owner_type: viewMetadata.placementOwnerType,
        search_type: viewMetadata.searchType,
      })
    },
    [sendAnalyticsEvent],
  )

  const trackNewVisited = useCallback(
    (visibility: SavedViewVisibility, viewMetadata?: ViewNewVisitMetadata) => {
      if (!viewMetadata) {
        sendAnalyticsEvent(SAVED_VIEW_EVENTS.newVisited, ANALYTICS_TARGET, {visibility})
        return
      }

      sendAnalyticsEvent(VIEW_EVENTS.newVisited, VIEW_ANALYTICS_TARGET, {
        visibility: viewMetadata.visibilityIsDefault ? 'default' : viewVisibility(visibility),
        placement_owner_type: viewMetadata.placementOwnerType,
        search_type: viewMetadata.searchType,
      })
    },
    [sendAnalyticsEvent],
  )

  const trackViewOpened = useCallback(
    (
      shortcutId: SavedViewId,
      visibility: SavedViewVisibility,
      source: SavedViewOpenSource,
      openedByCreator: boolean,
      viewMetadata?: ViewOpenMetadata,
    ) => {
      if (!viewMetadata) {
        sendAnalyticsEvent(SAVED_VIEW_EVENTS.viewOpened, ANALYTICS_TARGET, {
          shortcut_id: shortcutId,
          visibility,
          source,
          opened_by_creator: openedByCreator,
        })
        return
      }

      sendAnalyticsEvent(VIEW_EVENTS.viewOpened, VIEW_ANALYTICS_TARGET, {
        view_id: viewMetadata.viewId,
        visibility: viewVisibility(visibility),
        source,
        opened_by_creator: openedByCreator,
        placement_owner_type: viewMetadata.placementOwnerType,
        search_type: viewMetadata.searchType,
      })
    },
    [sendAnalyticsEvent],
  )

  const trackEditOpened = useCallback(
    (shortcutId: SavedViewId, visibility: SavedViewVisibility, openedByCreator: boolean) => {
      sendAnalyticsEvent(SAVED_VIEW_EVENTS.editOpened, ANALYTICS_TARGET, {
        shortcut_id: shortcutId,
        visibility,
        opened_by_creator: openedByCreator,
      })
    },
    [sendAnalyticsEvent],
  )

  const trackViewUpdated = useCallback(
    (shortcutId: SavedViewId, visibility: SavedViewVisibility, source: SavedViewUpdateSource) => {
      sendAnalyticsEvent(SAVED_VIEW_EVENTS.viewUpdated, ANALYTICS_TARGET, {shortcut_id: shortcutId, visibility, source})
    },
    [sendAnalyticsEvent],
  )

  const trackViewPinned = useCallback(
    (shortcutId: SavedViewId, visibility: SavedViewVisibility, source: SavedViewPinSource) => {
      sendAnalyticsEvent(SAVED_VIEW_EVENTS.viewPinned, ANALYTICS_TARGET, {shortcut_id: shortcutId, visibility, source})
    },
    [sendAnalyticsEvent],
  )

  const trackViewUnpinned = useCallback(
    (shortcutId: SavedViewId, visibility: SavedViewVisibility, source: SavedViewPinSource) => {
      sendAnalyticsEvent(SAVED_VIEW_EVENTS.viewUnpinned, ANALYTICS_TARGET, {
        shortcut_id: shortcutId,
        visibility,
        source,
      })
    },
    [sendAnalyticsEvent],
  )

  return {
    trackIndexVisited,
    trackNewVisited,
    trackViewOpened,
    trackEditOpened,
    trackViewUpdated,
    trackViewPinned,
    trackViewUnpinned,
  }
}
